import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getAgendaEventsService,
  createAgendaEventService,
  updateAgendaEventService,
  patchAgendaEventService,
  deleteAgendaEventService,
  getMisEstudiantesService,
} from "../services/agenda/agenda.service";
import { ROLES } from "../routes/role.constants";

/**
 * Hook para gestionar la lógica de la agenda.
 * Encapsula la comunicación con la API, permisos por rol y estado local.
 */
const useAgenda = () => {
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.rol?.trim();

  // Determinar permisos por rol
  const isMentor = userRole === ROLES.MENTOR;
  const isSuperuser = userRole === ROLES.ADMIN_SYS;
  const canAssignStudent = isMentor || isSuperuser;

  // Estado
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [search, setSearch] = useState("");
  const [ordering, setOrdering] = useState("-fecha_hora_inicio");
  const [estudiantes, setEstudiantes] = useState([]);
  const [loadingEstudiantes, setLoadingEstudiantes] = useState(false);

  // Cargar eventos
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAgendaEventsService({
        mes: selectedMonth,
        search,
        ordering,
      });
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Error al cargar la agenda";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, search, ordering]);

  // Cargar estudiantes (solo mentores/superusuarios)
  const fetchEstudiantes = useCallback(async () => {
    if (!canAssignStudent) return;
    setLoadingEstudiantes(true);
    try {
      const data = await getMisEstudiantesService();
      setEstudiantes(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silencioso si no tiene permiso (403)
      if (err.response?.status !== 403) {
        console.error("Error al cargar estudiantes:", err);
      }
      setEstudiantes([]);
    } finally {
      setLoadingEstudiantes(false);
    }
  }, [canAssignStudent]);

  // Cargar eventos al cambiar filtros
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Cargar estudiantes al montar (si tiene permiso)
  useEffect(() => {
    fetchEstudiantes();
  }, [fetchEstudiantes]);

  // Crear evento
  const createEvent = async (payload) => {
    try {
      const newEvent = await createAgendaEventService(payload);
      toast.success("Evento creado exitosamente");
      await fetchEvents();
      return newEvent;
    } catch (err) {
      const msg = extractErrorMessage(err, "Error al crear el evento");
      toast.error(msg);
      throw err;
    }
  };

  // Actualizar evento (PUT)
  const updateEvent = async (id, payload) => {
    try {
      const updated = await updateAgendaEventService(id, payload);
      toast.success("Evento actualizado exitosamente");
      await fetchEvents();
      return updated;
    } catch (err) {
      const msg = extractErrorMessage(err, "Error al actualizar el evento");
      toast.error(msg);
      throw err;
    }
  };

  // Actualizar evento parcial (PATCH)
  const patchEvent = async (id, payload) => {
    try {
      const updated = await patchAgendaEventService(id, payload);
      toast.success("Evento actualizado exitosamente");
      await fetchEvents();
      return updated;
    } catch (err) {
      const msg = extractErrorMessage(err, "Error al actualizar el evento");
      toast.error(msg);
      throw err;
    }
  };

  // Eliminar evento
  const deleteEvent = async (id) => {
    try {
      await deleteAgendaEventService(id);
      toast.success("Evento eliminado exitosamente");
      await fetchEvents();
    } catch (err) {
      const msg = extractErrorMessage(err, "Error al eliminar el evento");
      toast.error(msg);
      throw err;
    }
  };

  // Navegar entre meses
  const goToPrevMonth = () => {
    setSelectedMonth((prev) => {
      const [year, month] = prev.split("-").map(Number);
      const date = new Date(year, month - 2, 1);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    });
  };

  const goToNextMonth = () => {
    setSelectedMonth((prev) => {
      const [year, month] = prev.split("-").map(Number);
      const date = new Date(year, month, 1);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    });
  };

  const goToToday = () => {
    const now = new Date();
    setSelectedMonth(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    );
  };

  return {
    // Estado
    events,
    loading,
    error,
    selectedMonth,
    search,
    ordering,
    estudiantes,
    loadingEstudiantes,

    // Permisos
    canAssignStudent,
    isMentor,
    isSuperuser,
    userRole,

    // Acciones de filtro
    setSelectedMonth,
    setSearch,
    setOrdering,
    goToPrevMonth,
    goToNextMonth,
    goToToday,

    // CRUD
    createEvent,
    updateEvent,
    patchEvent,
    deleteEvent,
    fetchEvents,
  };
};

/**
 * Extrae un mensaje de error legible desde la respuesta del API.
 */
function extractErrorMessage(err, fallback) {
  const data = err.response?.data;
  if (typeof data === "string") return data;
  if (data?.detail) return data.detail;
  if (data?.message) return data.message;
  // Caso: errores de validación { field: ["msg1", "msg2"] }
  if (typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    if (firstKey) {
      const val = data[firstKey];
      if (Array.isArray(val)) return val[0];
      if (typeof val === "string") return val;
    }
  }
  return fallback;
}

export default useAgenda;
